import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Home, Users, Wrench, Receipt, AlertTriangle, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";

const BANNER =
  "https://images.pexels.com/photos/33803734/pexels-photo-33803734.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function LandlordDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState({ maintenance: [], rents: [] });

  useEffect(() => {
    api.get("/dashboard/landlord").then((r) => setStats(r.data)).catch(() => setStats({}));
    Promise.all([
      api.get("/maintenance").catch(() => ({ data: [] })),
      api.get("/rents").catch(() => ({ data: [] })),
    ]).then(([m, r]) => setRecent({ maintenance: m.data.slice(0, 5), rents: r.data.slice(0, 5) }));
  }, []);

  const tiles = [
    { label: "Properties", icon: Home, value: stats?.properties, hint: "total units", testid: "stat-properties" },
    { label: "Active Leases", icon: Users, value: stats?.leases, hint: `${stats?.occupancy_rate ?? 0}% occupancy`, testid: "stat-leases" },
    { label: "Pending Maintenance", icon: Wrench, value: stats?.pending_maintenance, hint: "needs attention", testid: "stat-maintenance" },
    { label: "Unpaid This Month", icon: Receipt, value: stats?.unpaid_rent_this_month, hint: "outstanding", testid: "stat-unpaid" },
    { label: "Total Incidents", icon: AlertTriangle, value: stats?.total_incidents, hint: "on record", testid: "stat-incidents" },
    { label: "Occupancy", icon: TrendingUp, value: `${stats?.occupancy_rate ?? 0}%`, hint: "current rate", testid: "stat-occupancy" },
  ];

  return (
    <AppLayout title="Dashboard" subtitle="A bird's-eye view of your portfolio">
      {/* Editorial top banner */}
      <div className="relative h-48 mb-10 overflow-hidden" data-testid="dashboard-banner">
        <img src={BANNER} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/55 to-transparent" />
        <div className="relative h-full px-10 flex flex-col justify-center text-ivory">
          <div className="micro-label text-gold mb-2">Vol. I · The Editorial</div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-xl tracking-tight">
            Welcome back, <em className="text-gold not-italic font-normal">{user?.name?.split(" ")[0] || "Landlord"}</em>.
          </h2>
          <div className="mt-3 flex items-center gap-3 text-[10px] tracking-[0.25em] uppercase opacity-80">
            <span className="h-px w-10 bg-gold" />
            <span>A composed overview of every estate in your care.</span>
          </div>
        </div>
      </div>

      {/* Stat cards — editorial */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-bone mb-12" data-testid="stats-grid">
        {tiles.map((t) => (
          <Card
            key={t.label}
            data-testid={t.testid}
            className="rounded-none border-0 bg-white p-7 transition-all duration-500 hover:bg-ivory group relative"
          >
            <div className="flex items-start justify-between">
              <div className="space-y-3">
                <div className="micro-label text-slate-500">{t.label}</div>
                <div className="font-display text-5xl tracking-tight text-ink leading-none">
                  {stats == null ? <Skeleton className="h-10 w-20" /> : (t.value ?? 0)}
                </div>
                <div className="text-xs text-slate-500 italic">— {t.hint}</div>
              </div>
              <div className="h-10 w-10 border border-bone flex items-center justify-center group-hover:border-gold group-hover:bg-gold/10 transition-colors duration-500">
                <t.icon className="h-4 w-4 text-ink" strokeWidth={1.5} />
              </div>
            </div>
            <div className="absolute bottom-0 left-7 right-7 h-px bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
          </Card>
        ))}
      </div>

      {/* Recent lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="rounded-none border border-bone bg-white p-7" data-testid="recent-maintenance">
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-bone">
            <div>
              <div className="micro-label text-gold mb-1">The Workshop</div>
              <h3 className="font-display text-xl tracking-tight">Recent Maintenance</h3>
            </div>
            <span className="text-xs text-slate-500 font-mono-num">{recent.maintenance.length} items</span>
          </div>
          {recent.maintenance.length === 0 ? (
            <Empty label="No maintenance requests yet." />
          ) : (
            <ul className="divide-y divide-bone">
              {recent.maintenance.map((m) => (
                <li key={m.id} className="py-3 flex items-start gap-3">
                  <Wrench className="h-4 w-4 text-gold mt-0.5" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{m.title}</div>
                    <div className="text-xs text-slate-500">{m.property_name} · {m.priority} · {m.status}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="rounded-none border border-bone bg-white p-7" data-testid="recent-rents">
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-bone">
            <div>
              <div className="micro-label text-gold mb-1">The Ledger</div>
              <h3 className="font-display text-xl tracking-tight">Recent Rent Records</h3>
            </div>
            <span className="text-xs text-slate-500 font-mono-num">{recent.rents.length} entries</span>
          </div>
          {recent.rents.length === 0 ? (
            <Empty label="No rent records yet." />
          ) : (
            <ul className="divide-y divide-bone">
              {recent.rents.map((r) => (
                <li key={r.id} className="py-3 flex items-center gap-3">
                  <Receipt className="h-4 w-4 text-gold" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {r.tenant_name} <span className="text-slate-400 font-normal">· {r.property_name}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {r.period_month} · <span className="font-mono-num">${r.amount}</span>
                    </div>
                  </div>
                  <span
                    className={`text-[10px] px-2 py-0.5 uppercase tracking-widest font-semibold ${
                      r.status === "paid" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-rose-50 text-rose-700 border border-rose-200"
                    }`}
                  >
                    {r.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}

function Empty({ label }) {
  return <div className="text-sm text-slate-400 py-6 text-center italic">{label}</div>;
}
